import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { detectUserLanguage, extractLanguageFromPath, needsLanguagePrefix } from '@/lib/language-detection';
import { useDocumentTitle } from '@/hooks/use-document-title';

/**
 * 语言检测测试页面
 * 用于验证语言检测功能是否正常工作
 */
const LanguageTestPage = () => {
  const location = useLocation();
  const { t, i18n } = useTranslation('common');
  const [detectedLanguage, setDetectedLanguage] = useState<string>('');
  const [currentPath, setCurrentPath] = useState<string>('');
  const [needsPrefix, setNeedsPrefix] = useState<boolean>(false);
  const [extractedLanguage, setExtractedLanguage] = useState<string | null>(null);
  const [currentTitle, setCurrentTitle] = useState<string>('');
  
  // 设置页面标题
  useDocumentTitle('语言测试页面');

  useEffect(() => {
    const path = location.pathname;
    setCurrentPath(path);
    
    const detected = detectUserLanguage();
    setDetectedLanguage(detected);
    
    const needs = needsLanguagePrefix(path);
    setNeedsPrefix(needs);
    
    const extracted = extractLanguageFromPath(path);
    setExtractedLanguage(extracted);
    
    // 更新当前标题显示
    setCurrentTitle(document.title);
  }, [location.pathname, i18n.language]);

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold mb-6">语言检测测试页面</h1>
      
      <div className="space-y-4">
        <div className="bg-gray-100 p-4 rounded-lg">
          <h2 className="text-xl font-semibold mb-2">当前路径信息</h2>
          <p><strong>当前路径:</strong> {currentPath}</p>
          <p><strong>需要语言前缀:</strong> {needsPrefix ? '是' : '否'}</p>
          <p><strong>从路径提取的语言:</strong> {extractedLanguage || '无'}</p>
        </div>
        
        <div className="bg-blue-100 p-4 rounded-lg">
          <h2 className="text-xl font-semibold mb-2">语言检测结果</h2>
          <p><strong>检测到的语言:</strong> {detectedLanguage}</p>
        </div>
        
        <div className="bg-green-100 p-4 rounded-lg">
          <h2 className="text-xl font-semibold mb-2">浏览器信息</h2>
          <p><strong>浏览器语言:</strong> {navigator.language}</p>
          <p><strong>浏览器语言列表:</strong> {navigator.languages?.join(', ') || '无'}</p>
        </div>
        
        <div className="bg-purple-100 p-4 rounded-lg">
          <h2 className="text-xl font-semibold mb-2">页面标题测试</h2>
          <p><strong>当前页面标题:</strong> {currentTitle}</p>
          <p><strong>当前i18n语言:</strong> {i18n.language}</p>
          <p><strong>基础标题翻译:</strong> {t('title')}</p>
          <p><strong>概览标题翻译:</strong> {t('overview.title')}</p>
          <p><strong>记忆标题翻译:</strong> {t('memory.title')}</p>
        </div>
        
        <div className="bg-yellow-100 p-4 rounded-lg">
          <h2 className="text-xl font-semibold mb-2">测试说明</h2>
          <ul className="list-disc list-inside space-y-1">
            <li>访问 <code>/</code> 应该重定向到 <code>/zh/dashboard</code> 或 <code>/en/dashboard</code></li>
            <li>访问 <code>/dashboard</code> 应该重定向到 <code>/zh/dashboard</code> 或 <code>/en/dashboard</code></li>
            <li>访问 <code>/zh/dashboard</code> 或 <code>/en/dashboard</code> 应该正常显示</li>
            <li>语言检测优先级：URL路径 → 浏览器语言 → localStorage → 默认中文</li>
            <li>页面标题应该根据当前语言动态变化</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default LanguageTestPage;
